import React from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'destructive' | 'outline' | 'link' | 'icon'

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  children: React.ReactNode
}

const baseStyles =
  'inline-flex items-center justify-center min-w-[125px] h-[40px] rounded-full py-2.5 px-6 gap-2 text-sm font-semibold tracking-normal align-middle transition-all duration-150 cursor-pointer whitespace-nowrap'

const variantStyles: Record<ButtonVariant, string> = {
  primary: [
    'bg-action text-white',
    'hover:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] hover:brightness-90',
    'focus-visible:outline-none focus-visible:border focus-visible:border-border focus-visible:shadow-[0_0_0_2px_#FFFFFF,0_0_0_4px_#27785B]',
    'active:bg-action active:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] active:brightness-100',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:hover:shadow-none',
  ].join(' '),

  secondary: [
    'bg-border text-text-heading',
    'hover:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] hover:brightness-95',
    'focus-visible:outline-none focus-visible:border focus-visible:border-border focus-visible:shadow-[0_0_0_2px_#FFFFFF,0_0_0_4px_#315776]',
    'active:bg-border active:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] active:brightness-100',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:hover:shadow-none',
  ].join(' '),

  destructive: [
    'bg-border text-action-destructive',
    'hover:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] hover:brightness-95',
    'focus-visible:outline-none focus-visible:border focus-visible:border-border focus-visible:shadow-[0_0_0_2px_#FFFFFF,0_0_0_4px_#CC1400]',
    'active:bg-border active:text-action-destructive active:brightness-90 active:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
    'disabled:bg-border disabled:text-action-destructive disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:hover:shadow-none',
  ].join(' '),

  outline: [
    'bg-white text-text-muted border border-border',
    'hover:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
    'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#FFFFFF,0_0_0_4px_#315776]',
    'active:bg-border active:text-text-heading active:border-border',
    'disabled:opacity-50 disabled:text-text-muted disabled:cursor-not-allowed disabled:hover:shadow-none',
  ].join(' '),

  link: [
    'bg-transparent text-action border-none shadow-none min-w-0 h-auto px-0',
    'hover:underline',
    'focus-visible:outline-none focus-visible:border focus-visible:border-action focus-visible:rounded-full focus-visible:px-6 focus-visible:min-w-[125px] focus-visible:h-[40px]',
    'active:text-action active:no-underline',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:no-underline',
  ].join(' '),

  icon: [
    '!min-w-0 !w-10 !h-10 !p-0 !px-0 !py-0 !gap-0 !rounded-full bg-action text-white shrink-0',
    'hover:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] hover:brightness-90',
    'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#FFFFFF,0_0_0_4px_#27785B]',
    'active:bg-action active:brightness-100',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:hover:shadow-none',
  ].join(' '),
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', className = '', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={`${baseStyles} ${variantStyles[variant]} ${className}`}
        {...props}
      >
        {children}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
